import type {
  FieldConfig,
  FieldGroup,
  SelectOption,
} from './QuestWorkspaceEditorLayout';
import {
  credentialStatuses,
  questStatuses,
  questTiers,
  roundDisplayName,
  roundStatuses,
  type QuestCredentialForm,
  type QuestForm,
  type QuestRoundAppForm,
  type QuestRoundForm,
  type QuestSeasonForm,
  type QuestSelectionPreviewForm,
  type QuestWorkspaceForm,
} from './questWorkspace';

export const seasonGroups: FieldGroup<QuestSeasonForm>[] = [
  {
    title: 'Basics',
    fields: [
      {
        key: 'blurb',
        label: 'Season Blurb',
        kind: 'textarea',
        rows: 3,
        translation: {
          metadataKey: 'metadataJson',
          i18nField: 'blurb',
        },
      },
      { key: 'rulesUrl', label: 'Rules URL', placeholder: 'https://...' },
    ],
  },
  {
    title: 'Claim window',
    description: 'When participants can claim rewards earned in this season.',
    fields: [
      {
        key: 'claimStartsAt',
        label: 'Claim Starts At',
        inputType: 'datetime-local',
      },
      {
        key: 'claimEndsAt',
        label: 'Claim Ends At',
        inputType: 'datetime-local',
      },
    ],
  },
];

export const seasonAdvancedFields: FieldConfig<QuestSeasonForm>[] = [
  {
    key: 'metadataJson',
    label: 'Metadata JSON',
    kind: 'textarea',
    rows: 5,
    mono: true,
  },
];

export function roundGroups(): FieldGroup<QuestRoundForm>[] {
  return [
    {
      title: 'Round basics',
      fields: [
        {
          key: 'roundNumber',
          label: 'Round Number',
          inputType: 'number',
          required: true,
        },
        {
          key: 'status',
          label: 'Status',
          kind: 'select',
          options: roundStatuses.map((status) => ({
            value: status,
            label: status,
          })),
        },
      ],
    },
    {
      title: 'Copy',
      description:
        'Shown across the round lifecycle — open, closing, complete.',
      fields: [
        {
          key: 'openBlurb',
          label: 'Open Blurb',
          hint: 'while live',
          kind: 'textarea',
          rows: 2,
          span: 'half',
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'openBlurb',
          },
        },
        {
          key: 'openBlurbSubtitle',
          label: 'Open Subtitle',
          kind: 'textarea',
          rows: 2,
          span: 'half',
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'openBlurbSubtitle',
          },
        },
        {
          key: 'closingBlurb',
          label: 'Closing Blurb',
          hint: 'winding down',
          kind: 'textarea',
          rows: 2,
          span: 'half',
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'closingBlurb',
          },
        },
        {
          key: 'closingBlurbSubtitle',
          label: 'Closing Subtitle',
          kind: 'textarea',
          rows: 2,
          span: 'half',
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'closingBlurbSubtitle',
          },
        },
        {
          key: 'completeBlurb',
          label: 'Complete Blurb',
          span: 'half',
          disabled: true,
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'completeBlurb',
          },
        },
        {
          key: 'completeBlurbSubtitle',
          label: 'Complete Subtitle',
          kind: 'textarea',
          rows: 2,
          span: 'half',
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'completeBlurbSubtitle',
          },
        },
      ],
    },
    {
      title: 'Schedule',
      description:
        'The round window and when results reveal. Display Ends At is visual only. All times are UTC.',
      fields: [
        {
          key: 'startsAt',
          label: 'Starts At',
          inputType: 'datetime-local',
          required: true,
        },
        {
          key: 'endsAt',
          label: 'Ends At',
          inputType: 'datetime-local',
          required: true,
        },
        {
          key: 'displayEndsAt',
          label: 'Display Ends At',
          hint: 'optional; visual only',
          inputType: 'datetime-local',
        },
        {
          key: 'revealStartsAt',
          label: 'Reveal Starts At',
          inputType: 'datetime-local',
        },
        {
          key: 'revealEndsAt',
          label: 'Reveal Ends At',
          inputType: 'datetime-local',
        },
      ],
    },
    {
      title: 'Rewards',
      description: 'Amounts are expressed in SKR.',
      fields: [
        {
          key: 'upperBoundSkrBaseUnits',
          label: 'Upper Bound SKR',
          placeholder: '100',
        },
      ],
    },
  ];
}

export const roundAdvancedFields: FieldConfig<QuestRoundForm>[] = [
  {
    key: 'eligibilityStartsAt',
    label: 'Eligibility Starts At',
    inputType: 'datetime-local',
  },
  {
    key: 'eligibilityEndsAt',
    label: 'Eligibility Ends At',
    inputType: 'datetime-local',
  },
  {
    key: 'selectionConfigJson',
    label: 'Selection Config JSON',
    kind: 'textarea',
    rows: 6,
    mono: true,
  },
  {
    key: 'metadataJson',
    label: 'Metadata JSON',
    kind: 'textarea',
    rows: 6,
    mono: true,
  },
];

export function appGroups(
  roundOptions: SelectOption[],
): FieldGroup<QuestRoundAppForm>[] {
  return [
    {
      title: 'Placement',
      fields: [
        {
          key: 'roundId',
          label: 'Round',
          kind: 'select',
          options: roundOptions,
        },
      ],
    },
    {
      title: 'Media & links',
      fields: [
        { key: 'iconUrl', label: 'Icon URL', placeholder: 'https://...' },
        {
          key: 'deepLinkUrl',
          label: 'Deep Link URL',
          placeholder: 'app://quest',
        },
      ],
    },
    {
      title: 'Curation',
      fields: [
        {
          key: 'curationNote',
          label: 'Curation Note',
          kind: 'textarea',
          rows: 3,
          required: true,
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'curationNote',
          },
        },
      ],
    },
  ];
}

export const appAdvancedFields: FieldConfig<QuestRoundAppForm>[] = [
  {
    key: 'featuredReviewJson',
    label: 'Featured Review JSON',
    kind: 'textarea',
    rows: 5,
    mono: true,
  },
  {
    key: 'metadataJson',
    label: 'Metadata JSON',
    kind: 'textarea',
    rows: 5,
    mono: true,
  },
];

export function questGroups(): FieldGroup<QuestForm>[] {
  return [
    {
      title: 'Basics',
      fields: [
        {
          key: 'title',
          label: 'Title',
          required: true,
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'title',
          },
        },
        {
          key: 'questKey',
          label: 'Quest ID',
          placeholder: 'generated_from_title',
          readOnly: true,
          hint: 'auto from title',
        },
        {
          key: 'tier',
          label: 'Tier',
          kind: 'select',
          options: questTiers.map((tier) => ({ value: tier, label: tier })),
        },
        {
          key: 'status',
          label: 'Status',
          kind: 'select',
          options: questStatuses.map((status) => ({
            value: status,
            label: status,
          })),
        },
      ],
    },
    {
      title: 'Content',
      fields: [
        {
          key: 'description',
          label: 'Description',
          kind: 'textarea',
          rows: 4,
          required: true,
          translation: {
            metadataKey: 'metadataJson',
            i18nField: 'description',
          },
        },
        // {
        //   key: 'completionCriteria',
        //   label: 'Completion Criteria',
        //   kind: 'textarea',
        //   rows: 4,
        //   required: true,
        // },
        {
          key: 'deepLinkUrl',
          label: 'Deep Link URL',
          placeholder: 'app://quest',
        },
      ],
    },
    {
      title: 'Badge',
      fields: [
        { key: 'badgeTitle', label: 'Badge Title', readOnly: true },
        {
          key: 'badgeImageUrl',
          label: 'Badge Image URL',
          placeholder: 'https://...',
        },
      ],
    },
  ];
}

export const questAdvancedFields: FieldConfig<QuestForm>[] = [
  // {
  //   key: 'tipsJson',
  //   label: 'Tips JSON',
  //   kind: 'textarea',
  //   rows: 5,
  //   mono: true,
  // },
  {
    key: 'badgeMetadataJson',
    label: 'Badge Metadata JSON',
    kind: 'textarea',
    rows: 5,
    mono: true,
  },
  {
    key: 'metadataJson',
    label: 'Metadata JSON',
    kind: 'textarea',
    rows: 5,
    mono: true,
  },
];

export const credentialGroups: FieldGroup<QuestCredentialForm>[] = [
  {
    title: 'Key status',
    description: 'Revoke or re-activate this API key.',
    fields: [
      {
        key: 'status',
        label: 'Status',
        kind: 'select',
        options: credentialStatuses.map((status) => ({
          value: status,
          label: status,
        })),
      },
    ],
  },
];

export const credentialSigningFields: FieldConfig<QuestCredentialForm>[] = [
  {
    key: 'signaturePublicKey',
    label: 'Signature Public Key',
    kind: 'textarea',
    rows: 3,
    mono: true,
  },
];

export const credentialAdvancedFields: FieldConfig<QuestCredentialForm>[] = [
  {
    key: 'metadataJson',
    label: 'Metadata JSON',
    kind: 'textarea',
    rows: 5,
    mono: true,
  },
];

export function selectionGroups(
  roundOptions: SelectOption[],
): FieldGroup<QuestSelectionPreviewForm>[] {
  return [
    {
      title: 'Run summary',
      fields: [
        {
          key: 'selectedRoundId',
          label: 'Round',
          kind: 'select',
          options: roundOptions,
        },
        {
          key: 'candidateCount',
          label: 'Candidate Count',
          inputType: 'number',
          readOnly: true,
        },
        {
          key: 'selectedCount',
          label: 'Selected Count',
          inputType: 'number',
          readOnly: true,
        },
      ],
    },
    {
      title: 'Reward tier',
      description: 'Amounts are expressed in SKR.',
      fields: [
        { key: 'tierCode', label: 'Tier Code', placeholder: 'top_100' },
        { key: 'amountBaseUnits', label: 'Amount SKR', placeholder: '100' },
      ],
    },
  ];
}

export const selectionAdvancedFields: FieldConfig<QuestSelectionPreviewForm>[] =
  [
    {
      key: 'criteriaSnapshotJson',
      label: 'Criteria Snapshot JSON',
      kind: 'textarea',
      rows: 7,
      mono: true,
      readOnly: true,
    },
    {
      key: 'inputSnapshotJson',
      label: 'Input Snapshot JSON',
      kind: 'textarea',
      rows: 7,
      mono: true,
      readOnly: true,
    },
    {
      key: 'recipientSnapshotJson',
      label: 'Recipient Snapshot JSON',
      kind: 'textarea',
      rows: 7,
      mono: true,
      readOnly: true,
    },
  ];

export function roundOptionsOf(workspace: QuestWorkspaceForm): SelectOption[] {
  return workspace.rounds.map((round, index) => ({
    value: round.id,
    label: roundDisplayName(round, index),
  }));
}
